import {
  css, type SerializedStyles,
} from '@emotion/react';
import {
  autoUpdate, flip,
  type Placement, useFloating,
} from '@floating-ui/react';
import {
  type ReactNode, useCallback, useContext, useState,
} from 'react';
import type { Theme } from '~/_shared/themes/theme.model';
import { ContextMenuClosingContext } from './contextMenuClosingContext';
import { ContextMenuContentComponent } from './contextMenuContent.component';

type Props = Readonly<{
  children: ReactNode;
  renderSubmenu?: () => ReactNode;
  showSubmenuOnClick?: boolean;
  className?: string;
  onClick?: () => void;
  closeOnClick?: boolean;
  itemStyle?: SerializedStyles;
  submenuStyle?: SerializedStyles;
  subMenuMaxWidth?: number;
  preferredPlacement?: Placement;
}>;

const menuItemStyle = (theme: Theme) => css({
  borderTop: `1px solid ${theme.lineColors.basePrimary}`,
  '&:first-of-type': {
    borderTop: 'none',
  },
});

export const ContextMenuItemCoreComponent = ({
  children,
  className,
  itemStyle,
  onClick,
  preferredPlacement = 'right-start',
  renderSubmenu,
  closeOnClick = !renderSubmenu,
  showSubmenuOnClick,
  subMenuMaxWidth,
  submenuStyle,
  ...restProps
}: Props) => {
  const [isSubmenuVisible, setIsSubmenuVisible] = useState(false);
  const { refs, floatingStyles } = useFloating({
    placement: preferredPlacement,
    middleware: [flip()],
    whileElementsMounted: autoUpdate,
  });
  const { closeContextMenu } = useContext(ContextMenuClosingContext);

  const handleHide = useCallback(() => setIsSubmenuVisible(false), []);

  return (
    <div
      ref={refs.setReference}
      css={menuItemStyle}
      className={className}
      onMouseEnter={() => !showSubmenuOnClick && setIsSubmenuVisible(true)}
      onMouseLeave={() => !showSubmenuOnClick && setIsSubmenuVisible(false)}
      {...restProps}
    >
      <div
        css={itemStyle}
        onClick={event => {
          event.stopPropagation();
          event.preventDefault(); // without this accordion collapse is triggered if in accordion header
          onClick?.();
          if (closeOnClick) {
            closeContextMenu();
          }
          if (showSubmenuOnClick) {
            setIsSubmenuVisible(!isSubmenuVisible);
          }
        }}
      >
        {children}
      </div>
      {renderSubmenu && (
        <ContextMenuContentComponent
          css={submenuStyle}
          ref={refs.setFloating}
          isVisible={isSubmenuVisible}
          onHide={handleHide}
          style={floatingStyles}
          maxWidth={subMenuMaxWidth}
        >
          {renderSubmenu()}
        </ContextMenuContentComponent>
      )}
    </div>
  );
};
